import { QueryHookOptions, gql } from "@apollo/client";
import { useMemo } from "react";

import { ConsumerApiError } from "../custom-events";
import { useQuery } from "../hooks";
import { findAmazonRequestIdForGraphQLError } from "../utils/findAmazonRequestIdForGraphQLError";
import { BookingQuestionFragment } from "./graphql";
import type { Price } from "./types/Price";

const CACHE_AVAILABILITY_QUERY = gql`
    query useAvailability($id: String!, $input: AvailabilityInput, $autoFillOptions: Boolean!) {
        availability(id: $id, input: $input, autoFillOptions: $autoFillOptions) {
            id
            isValid
            minParticipants
            maxParticipants
            date
            soldOut
            optionList {
                total
                isComplete
                nodes {
                    id
                    isAnswered
                    label
                    level
                    helper
                    placeholder
                    dataType
                    answerValue
                    answerFormattedText
                    availableOptions {
                        label
                        value
                    }
                }
            }
            unitPrice {
                grossFormattedText
                netFormattedText
                gross
                currency
                pricingData
            }
            totalPrice {
                grossFormattedText
                gross
                currency
                netFormattedText
                commissionFormattedText
            }
            pricingCategoryList {
                recordCount
                errors
                nodes {
                    id
                    errors
                    units
                    label
                    description
                    minParticipants
                    maxParticipants
                    maxParticipantsDepends {
                        pricingCategoryId
                        multiplier
                        explanation
                    }
                    minAge
                    maxAge
                    discountsAvailable
                    isDiscounted
                    unitPrice {
                        grossFormattedText
                        gross
                        currency
                        netFormattedText
                        commissionFormattedText
                        pricingData
                    }
                    totalDiscountApplied {
                        grossFormattedText
                        gross
                        currency
                        netFormattedText
                        commissionFormattedText
                    }
                    roomPackage {
                        roomList {
                            nodes {
                                name
                                bedGroupList {
                                    nodes {
                                        id
                                        name
                                    }
                                }
                                occupancy
                                rateAmenityList {
                                    nodes {
                                        id
                                        name
                                    }
                                }
                                viewList {
                                    nodes {
                                        id
                                        name
                                    }
                                }
                                roomAmenityList {
                                    nodes {
                                        id
                                        name
                                    }
                                }
                                quantity
                            }
                        }
                    }
                    disabled
                }
            }
        }
    }
`;

export type AvailabilityOptionList = {
    isComplete: boolean;
    nodes: BookingQuestionFragment[];
};

export type AvailabilityPricingCategory = {
    id: string;
    errors: string[];
    units: number;
    isDiscounted: boolean;
    discountsAvailable: boolean;
    label: string;
    description: string | null;
    totalDiscountApplied: Price;
    minParticipants: number | null;
    maxParticipants: number | null;
    maxParticipantsDepends: Array<{
        pricingCategoryId: string;
        explanation: string;
    }> | null;
    minAge: number | null;
    maxAge: number | null;
    unitPrice: Price;
    roomPackage?: {
        roomList: {
            nodes: AvailabilityPricingCategoryRoom[];
        };
    };
    disabled: boolean;
};

export type AvailabilityPricingCategoryRoom = {
    occupancy: string;
    name: string;
    rateAmenityList: {
        nodes: {
            id: string;
            name: string;
        }[];
    };
    bedGroupList: {
        nodes: {
            id: string;
            name: string;
        }[];
    };
    viewList: {
        nodes: {
            id: string;
            name: string;
        }[];
    };
    roomAmenityList: {
        nodes: {
            id: string;
            name: string;
        }[];
    };
    quantity: number;
};

export type AvailabilityPricingCategoryList = {
    errors: string[];
    nodes: AvailabilityPricingCategory[];
};

export interface Availability {
    id: string;
    date: string;
    soldOut: boolean;
    isValid: boolean;
    minParticipants: number | null;
    maxParticipants: number | null;
    totalPrice: Price;
    unitPrice: Price;
    optionList: AvailabilityOptionList;
    pricingCategoryList: null | AvailabilityPricingCategoryList;
}

export type AvailabilityInput = {
    optionList: Array<{ id: string; value: string }>;
    pricingCategoryList: Array<{ id: string; units: number }>;
};

type CacheAvailabilityOptions = QueryHookOptions<
    { availability: Availability },
    {
        id: string;
        input?: AvailabilityInput;
        autoFillOptions?: boolean;
        saleCurrency?: string;
    }
>;
export const useCacheAvailability = (options: CacheAvailabilityOptions) => {
    const saleCurrency = options.variables?.saleCurrency;
    const context = useMemo(() => ({ headers: { "X-Holibob-Currency": saleCurrency } }), [saleCurrency]);
    return useQuery(CACHE_AVAILABILITY_QUERY, {
        fetchPolicy: "no-cache",
        nextFetchPolicy: "no-cache",
        ...options,
        variables: {
            ...options.variables!,
            autoFillOptions: true,
        },
        onError: (error) => {
            const requestId = findAmazonRequestIdForGraphQLError(error);

            document.body.dispatchEvent(
                new ConsumerApiError({
                    code: "AVAILABILITY",
                    availabilityId: options.variables?.id ?? "UNKNOWN",
                    errorMessage: error.message,
                    requestId,
                })
            );
            options.onError?.(error);
        },
        context,
    });
};
